import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useConfirm } from '../../components/common/ConfirmProvider';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FiSave, 
  FiTrash2, 
  FiArrowLeft, 
  FiMapPin, 
  FiX,
  FiCheck,
  FiAlertCircle,
  FiPlus
} from 'react-icons/fi';
import NoteEditor from '../../components/notes/NoteEditor';
import CourseSelect from '../../components/notes/CourseSelect';
import WeekSelect from '../../components/notes/WeekSelect';
import MyNotesSidebar from '../../components/notes/MyNotesSidebar';
import notesApi from '../../services/notesApi';

const PageContainer = styled.div`
  --space-8: 8px;
  --space-12: 12px;
  --space-16: 16px;
  --space-24: 24px;
  --page-gutter: 12px;

  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-24);

  height: auto;
  min-height: 0;
  max-height: none;
  overflow: visible;

  padding: 0 var(--page-gutter) var(--space-16);
  background: var(--background-secondary);

  @media (min-width: 768px) {
    --page-gutter: 16px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 320px 1fr;
    --page-gutter: 24px;
  }

  @media (min-width: 1280px) {
    --page-gutter: 32px;
  }

  @media (min-width: 1440px) {
    grid-template-columns: 360px 1fr;
  }

  @media (max-width: 1024px) {
    gap: var(--space-16);
    padding-bottom: var(--space-12);
  }

  @media (max-width: 640px) {
    padding: 0 var(--space-12) var(--space-12);
    gap: var(--space-12);
  }
`;

const EditorContainer = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: var(--space-24);
  box-shadow: 0 12px 28px rgba(28, 25, 23, 0.08);
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  height: 100%;
  overflow: hidden;
  min-width: 0;
  
  @media (max-width: 1024px) {
    border-radius: 0;
    border-left: none;
    border-right: none;
    border-top: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    padding: 20px;
    gap: var(--space-16);
  }
  
  @media (max-width: 640px) {
    padding: var(--space-16);
    gap: var(--space-12);
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    padding: var(--space-12);
    gap: var(--space-8);
  }
  
  @media (max-height: 600px) {
    padding: 16px;
    gap: 12px;
    border-radius: 0;
  }
  
  @media (max-height: 500px) {
    padding: 12px;
    gap: 8px;
  }
`;

const EditorHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-12);
  padding-bottom: var(--space-16);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    gap: var(--space-12);
    padding-bottom: var(--space-12);
  }
  
  @media (max-width: 640px) {
    gap: var(--space-8);
    padding-bottom: var(--space-8);
  }
  
  @media (max-width: 480px) {
    gap: var(--space-8);
    padding-bottom: var(--space-8);
  }
  
  @media (max-height: 600px) {
    gap: 12px;
    padding-bottom: 12px;
  }
  
  @media (max-height: 500px) {
    gap: 8px;
    padding-bottom: 8px;
  }
`;

const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-12);
  width: 100%;
  min-width: 0;
  
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-12);
  width: 100%;
  min-width: 0;
  
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  
  &:hover {
    background: var(--background-tertiary);
    border-color: var(--border-dark);
  }
`;

const TitleInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
  background: var(--background-primary);
  height: 48px;
  min-width: 0;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(120, 53, 15, 0.2);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
  
  @media (max-height: 600px) {
    height: 40px;
    padding: 8px 12px;
    font-size: 14px;
  }
  
  @media (max-height: 500px) {
    height: 36px;
    padding: 6px 10px;
    font-size: 14px;
  }
  
  @media (max-width: 640px) {
    width: 100%;
  }
`;


const SaveStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  white-space: nowrap;
  color: ${props => {
    if (props.status === 'saving') return 'var(--warning-color)';
    if (props.status === 'saved') return 'var(--success-color)';
    if (props.status === 'error') return 'var(--error-color)';
    return 'var(--text-muted)';
  }};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--background-primary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  min-height: 44px;
  
  &:hover {
    background: var(--background-secondary);
    border-color: var(--border-dark);
  }
  
  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    
    &:hover {
      background: var(--primary-dark);
      border-color: var(--primary-dark);
    }
  }
  
  &.danger {
    color: var(--error-color);
    border-color: var(--error-color);
    
    &:hover {
      background: rgba(252, 129, 129, 0.1);
      border-color: var(--error-dark);
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditorSurface = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  gap: 12px;
`;

const ResizableEditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 280px;
  overflow: hidden;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--background-primary);
`;

const EditorBody = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;

  /* Prevent outer scroll; let ProseMirror scroll */
  overflow: hidden;

  /* Keep padding here so the editor fills consistently */
  padding: 8px;

  display: flex;
  flex-direction: column;

  /* Ensure the editor container fills */
  .rte-tt-container {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
  }

  /* Ensure the actual editor fills */
  .ProseMirror {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
  }

  @media (max-width: 640px) {
    padding: 6px;
  }
`;

const ResizeHandle = styled.div`
  height: 12px;
  cursor: ns-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid var(--border-color);
  background: var(--background-secondary);
  touch-action: none;

  &::before {
    content: '';
    width: 48px;
    height: 4px;
    border-radius: 999px;
    background: var(--border-color);
  }

  @media (max-width: 640px) {
    height: 16px;
  }
`;

const EditorFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
  flex-shrink: 0;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
  
  @media (max-height: 600px) {
    padding-top: 12px;
    gap: 8px;
  }
  
  @media (max-height: 500px) {
    padding-top: 8px;
    gap: 6px;
  }

  @media (max-width: 640px) {
    justify-content: flex-start;
    flex-direction: column;
    align-items: stretch;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  border-radius: 12px;
  color: var(--error-color);
  font-size: 14px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

// eslint-disable-next-line import/first
// (All imports must be at top; removing body import)

export default function NoteEditorPage() {
  const confirm = useConfirm();
  const { noteId } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const autosaveTimeoutRef = useRef(null);
  const lastSavedContentRef = useRef(null);
  const [editorHeight, setEditorHeight] = useState(null);
  const [hasManualResize, setHasManualResize] = useState(false);
  const editorContainerRef = useRef(null);
  const editorHeaderRef = useRef(null);
  const editorFooterRef = useRef(null);
  const resizeStateRef = useRef({ active: false, startY: 0, startHeight: 0 });

  const isNewNote = !noteId || noteId === 'new';

  // Fetch note data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch courses
        const coursesResponse = await notesApi.getCourses();
        if (coursesResponse.success) {
          setCourses(coursesResponse.courses || []);
        }

        // Fetch note only when we have a valid id and it's not a new note
        if (noteId && !isNewNote) {
          const noteResponse = await notesApi.getNote(noteId);
          if (noteResponse.success) {
            setNote(noteResponse.note);
            lastSavedContentRef.current = JSON.stringify(noteResponse.note);
          } else {
            setError('Note not found');
          }
        } else {
          // Initialize new note
          setNote({
            title: '',
            course_id: null,
            week_number: null,
            content_json: { type: 'doc', content: [] },
            content_html: '',
            content_plain: '',
            is_pinned: false
          });
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load note');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [noteId, isNewNote]);

  // Autosave functionality
  const autosave = useCallback(async (contentData) => {
    if (!note) return;

    try {
      setSaving(true);
      setSaveStatus('saving');

      if (isNewNote) {
        const response = await notesApi.createNote({
          ...note,
          ...contentData
        });

        if (response.success) {
          navigate(`/student/notes/${response.note.id}`, { replace: true });
            setNote(response.note);
            lastSavedContentRef.current = JSON.stringify(response.note);
            setLastSavedAt(response.note?.updated_at || new Date().toISOString());
            setIsDirty(false);
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        } else {
          setError(response.message || 'Failed to create note');
          setSaveStatus('error');
        }
        return;
      }

      const autosaveResponse = await notesApi.autosaveNote(note.id, contentData);

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
      setLastSavedAt(autosaveResponse?.note?.updated_at || new Date().toISOString());
      setIsDirty(false);
    } catch (err) {
      console.error('Autosave error:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [note, isNewNote, navigate]);

  // Handle content changes
  const handleContentChange = useCallback((contentData) => {
    setNote(prev => ({ ...prev, ...contentData }));
    setIsDirty(true);
    
    // Clear existing timeout
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    
    // Set new autosave timeout (5 seconds)
    autosaveTimeoutRef.current = setTimeout(() => {
      autosave(contentData);
    }, 5000);
  }, [autosave]);

  // Handle field changes
  const handleFieldChange = useCallback((field, value) => {
    setNote(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  }, []);

  // Save note
  const saveNote = useCallback(async () => {
    if (!note) return;

    try {
      setSaving(true);
      setSaveStatus('saving');

      if (isNewNote) {
        // Create new note
        const response = await notesApi.createNote(note);
        if (response.success) {
          navigate(`/student/notes/${response.note.id}`, { replace: true });
          setNote(response.note);
          lastSavedContentRef.current = JSON.stringify(response.note);
          setLastSavedAt(response.note?.updated_at || new Date().toISOString());
          setIsDirty(false);
        } else {
          setError(response.message || 'Failed to create note');
          setSaveStatus('error');
          return;
        }
      } else {
        // Update existing note
        const response = await notesApi.updateNote(note.id, note);
        if (response.success) {
          setNote(response.note);
          lastSavedContentRef.current = JSON.stringify(response.note);
          setLastSavedAt(response.note?.updated_at || new Date().toISOString());
          setIsDirty(false);
        } else {
          setError(response.message || 'Failed to save note');
          setSaveStatus('error');
          return;
        }
      }
      
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [note, isNewNote, navigate]);

  // Delete note
  const deleteNote = async () => {
    if (!note || isNewNote) return;
    
    if (await confirm({ title: 'Delete note?', message: 'This note will be permanently deleted. This action cannot be undone.', confirmText: 'Delete', danger: true })) {
      try {
        await notesApi.deleteNote(note.id);
        navigate('/student/notes');
      } catch (err) {
        console.error('Delete error:', err);
        setError('Failed to delete note');
      }
    }
  };

  // Toggle pin
  const togglePin = async () => {
    if (!note || isNewNote) return;

    try {
      const response = note.is_pinned 
        ? await notesApi.unpinNote(note.id)
        : await notesApi.pinNote(note.id);
      
      if (response.success) {
        setNote(prev => ({ ...prev, is_pinned: response.is_pinned }));
      }
    } catch (err) {
      console.error('Pin toggle error:', err);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveNote();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [saveNote]);

  // Cleanup autosave timeout
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, []);


  const getEditorBounds = useCallback(() => {
    const container = editorContainerRef.current;
    const header = editorHeaderRef.current;
    const footer = editorFooterRef.current;
    const containerHeight = container?.clientHeight || 0;
    const headerHeight = header?.clientHeight || 0;
    const footerHeight = footer?.clientHeight || 0;
    const gapBuffer = 28;
    const availableHeight = Math.max(0, containerHeight - headerHeight - footerHeight - gapBuffer);
    const minHeight = window.innerWidth <= 640 ? 200 : 280;
    const maxHeight = Math.max(minHeight, availableHeight || minHeight);
    return { minHeight, maxHeight, availableHeight };
  }, []);

  useEffect(() => {
    const updateAutoHeight = () => {
      const { minHeight, maxHeight, availableHeight } = getEditorBounds();
      setEditorHeight((prev) => {
        if (hasManualResize && prev !== null) {
          return Math.min(Math.max(prev, minHeight), maxHeight);
        }
        if (!availableHeight) {
          return null;
        }
        return Math.min(Math.max(availableHeight, minHeight), maxHeight);
      });
    };

    updateAutoHeight();
    window.addEventListener('resize', updateAutoHeight);
    return () => window.removeEventListener('resize', updateAutoHeight);
  }, [getEditorBounds, hasManualResize]);

  const handleResizeStart = useCallback((event) => {
    const pointerEvent = event.touches ? event.touches[0] : event;
    const { availableHeight, minHeight } = getEditorBounds();
    resizeStateRef.current = {
      active: true,
      startY: pointerEvent.clientY,
      startHeight: editorHeight ?? availableHeight ?? minHeight
    };
    setHasManualResize(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [editorHeight, getEditorBounds]);

  const handleResizeMove = useCallback((event) => {
    if (!resizeStateRef.current.active) return;
    const pointerEvent = event.touches ? event.touches[0] : event;
    const delta = pointerEvent.clientY - resizeStateRef.current.startY;
    const { minHeight, maxHeight } = getEditorBounds();
    const nextHeight = Math.min(Math.max(resizeStateRef.current.startHeight + delta, minHeight), maxHeight);
    setEditorHeight(nextHeight);
  }, [getEditorBounds]);

  const handleResizeEnd = useCallback((event) => {
    if (!resizeStateRef.current.active) return;
    resizeStateRef.current.active = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  if (loading) {
    return (
      <LoadingOverlay>
        <div>Loading note...</div>
      </LoadingOverlay>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <EditorContainer>
          <ErrorMessage>
            <FiAlertCircle size={16} />
            {error}
          </ErrorMessage>
          <BackButton onClick={() => navigate('/student/notes')}>
            <FiArrowLeft size={16} />
            Back to Notes
          </BackButton>
        </EditorContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <MyNotesSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentNoteId={note?.id}
      />
      
        <EditorContainer ref={editorContainerRef}>
          <EditorHeader ref={editorHeaderRef}>
          <HeaderTopRow>
            <BackButton onClick={() => navigate('/student/notes')}>
              <FiArrowLeft size={16} />
              Back
            </BackButton>
            <ActionButton onClick={() => navigate('/student/notes/new')}>
              <FiPlus size={16} />
              New Note
            </ActionButton>
          </HeaderTopRow>

          <TitleInput
            type="text"
            placeholder="Note title..."
            value={note?.title || ''}
            onChange={(e) => handleFieldChange('title', e.target.value)}
          />

          <HeaderControlsRow>
            <CourseSelect
              value={note?.course_id}
              onChange={(value) => handleFieldChange('course_id', value)}
              courses={courses}
            />

            <WeekSelect
              value={note?.week_number}
              onChange={(value) => handleFieldChange('week_number', value)}
            />

            <SaveStatus status={saveStatus}>
              {saveStatus === 'saving' && <FiSave size={14} />}
              {saveStatus === 'saved' && <FiCheck size={14} />}
              {saveStatus === 'error' && <FiAlertCircle size={14} />}
              {isDirty && saveStatus === 'idle' && <FiAlertCircle size={14} />}
              {saveStatus === 'saving' && 'Saving...'}
              {saveStatus === 'saved' && 'Saved'}
              {saveStatus === 'error' && 'Error'}
              {isDirty && saveStatus === 'idle' && 'Unsaved changes'}
              {!isDirty && saveStatus === 'idle' && (
                lastSavedAt
                  ? `Last saved ${new Date(lastSavedAt).toLocaleString()}`
                  : 'All changes saved'
              )}
            </SaveStatus>
          </HeaderControlsRow>
        </EditorHeader>

        <EditorSurface>
          <ResizableEditorWrapper
            style={
              hasManualResize && editorHeight !== null
                ? { height: editorHeight }
                : undefined
            }
          >
            <EditorBody>
              <NoteEditor
                note={note}
                onChange={handleContentChange}
                placeholder="Start writing your note..."
                autoResize={false}
              />
            </EditorBody>
            <ResizeHandle
              onPointerDown={handleResizeStart}
              onPointerMove={handleResizeMove}
              onPointerUp={handleResizeEnd}
              onPointerCancel={handleResizeEnd}
            />
          </ResizableEditorWrapper>

          <EditorFooter ref={editorFooterRef}>
            <ActionButtonsContainer>
              {!isNewNote && (
                <>
                  <ActionButton onClick={togglePin}>
                    {note?.is_pinned ? <FiX size={16} /> : <FiMapPin size={16} />}
                    {note?.is_pinned ? 'Unpin' : 'Pin'}
                  </ActionButton>
                  
                  <ActionButton onClick={deleteNote} className="danger">
                    <FiTrash2 size={16} />
                    Delete
                  </ActionButton>
                </>
              )}
              
              <ActionButton 
                onClick={saveNote} 
                className="primary"
                disabled={saving}
              >
                <FiSave size={16} />
                {isNewNote ? 'Create Note' : 'Save Changes'}
              </ActionButton>
            </ActionButtonsContainer>
          </EditorFooter>
        </EditorSurface>
      </EditorContainer>
    </PageContainer>
  );
}
